<template>
    <div class="row mb-2">
        <div class="col-sm-12 text-right">
            <div class="auto-form-save">
                <div class="custom-control custom-switch">
                    <input :id="switchId" type="checkbox" class="custom-control-input" v-model="isEnabled" />
                    <label class="custom-control-label ix-text-small" :for="switchId">Sauvegarde automatique</label>
                </div>
            </div>
        </div>
    </div>
   
</template>

<script>
import Swal from 'sweetalert2';

export default {
    name: 'AutoFormSaveComponent',
    props: {
        formSelector: {
            type: String,
            required: true
        },
        interval: {
            type: Number,
            required: true
        },
        enabled: {
            type: Boolean,
            default: true
        },
        storageKey: {
            type: String,
            default: 'form-draft'
        }
    },
    data() {
        return {
            form: null,
            timer: null,
            intervalMs: this.interval ? this.interval : 30000,
            isEnabled: this.enabled,
            hasUnsavedChanges: false,
            draftDialogPending: false,
            isPaused: false
        }
    },
    computed: {
        switchId() {
            return `auto-save-switch-${this._uid}`;
        }
    },
    mounted() {
        this.form = document.querySelector(this.formSelector);

        // Permet aux autres composants d'attendre la fin de la question du brouillon (une seule modale Swal à la fois)
        this.$root.$on('request-draft-dialog-status', (callback) => {
            if (callback) callback(this.draftDialogPending);
        });

        // Suspension de l'enregistrement pendant une modale (le toast de sauvegarde la fermerait)
        this.$root.$on('autosave-pause', () => { this.isPaused = true; });
        this.$root.$on('autosave-resume', () => { this.isPaused = false; });
        
        // Vérifier s'il y a un brouillon à restaurer
        if (this.hasDraft()) {
            this.showRestoreDialog();
        }
        
        if (this.isEnabled) {
            this.startTimer();
        }
        
        // Écouter les changements de formulaire pour marquer qu'il y a eu des modifications
        this.form.addEventListener('input', () => {
            this.hasUnsavedChanges = true;
        });
        
      
    },
    watch: {
        isEnabled(newVal) {
            if (newVal) {
                this.startTimer();
            } else {
                this.clearTimer();
            }
        }
    },
        beforeDestroy() {
            this.clearTimer();
        },
        
        // Méthode publique pour nettoyer le brouillon (à appeler après soumission réussie)
        clearDraftOnSuccess() {
            this.clearDraft();
        },
    methods: {
        startTimer() {
            this.timer = setInterval(() => {
                this.promptSave();
            }, this.intervalMs);
        },
        clearTimer() {
            if (this.timer) {
                clearInterval(this.timer);
                this.timer = null;
            }
        },
        cssEscapeForAttrValue(value) {
            return String(value).replace(/\\/g, "\\\\").replace(/\[/g, "\\[").replace(/\]/g, "\\]");
        },
        getFieldLabel(el) {
            if (el.id) {
                const safe = this.cssEscapeForAttrValue(el.id);
                const byFor = this.form.querySelector('label[for="' + safe + '"]');
                if (byFor && byFor.textContent) return byFor.textContent.trim();
            }
            const group = el.closest('.form-group');
            if (group) {
                const anyLabel = group.querySelector('label');
                if (anyLabel && anyLabel.textContent) return anyLabel.textContent.trim();
            }
            return el.name || 'Champ requis';
        },
        findMissingRequiredFields() {
            const missing = [];
            const requiredEls = this.form.querySelectorAll('[required]');
            requiredEls.forEach((el) => {
                const tag = el.tagName;
                let val = '';
                if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') {
                    val = (el.value || '').trim();
                    if ((el.type === 'checkbox' || el.type === 'radio')) {
                        const name = el.name;
                        if (name) {
                            const group = this.form.querySelectorAll('input[name="' + name.replace(/([.*+?^${}()|[\]\\])/g, '\\$1') + '"]');
                            const anyChecked = Array.from(group).some(i => i.checked);
                            if (!anyChecked) {
                                missing.push(this.getFieldLabel(el));
                                return;
                            } else {
                                return;
                            }
                        }
                    }
                }
                if (!val) missing.push(this.getFieldLabel(el));
            });
            return missing;
        },
        showToast(message, type = 'error') {
            // Utilisation de SweetAlert2 pour les toasts
            const Toast = Swal.mixin({
                toast: true,
                position: 'top-end',
                showConfirmButton: false,
                timer: 3000,
                timerProgressBar: true,
                didOpen: (toast) => {
                    toast.addEventListener('mouseenter', Swal.stopTimer)
                    toast.addEventListener('mouseleave', Swal.resumeTimer)
                }
            });

            Toast.fire({
                icon: type,
                title: message
            });
        },
        saveFormData() {
            try {
                const formData = this.getFormData();
                const timestamp = new Date().toISOString();
                
                const draftData = {
                    formData: formData,
                    timestamp: timestamp,
                    url: window.location.href,
                    formId: this.form.id || 'unknown'
                };
                
                localStorage.setItem(this.storageKey, JSON.stringify(draftData));
                this.showToast('Brouillon sauvegardé localement', 'success');
                
                // Émettre un événement pour informer le parent de la sauvegarde
                this.$emit('autosave-success', {
                    timestamp: timestamp,
                    action: 'saved'
                });
            } catch (error) {
                console.error('Erreur lors de la sauvegarde locale:', error);
                this.showToast('Erreur lors de la sauvegarde locale', 'error');
            }
        },
        
        getFormData() {
            const formData = {};
            const formElements = this.form.querySelectorAll('input, textarea, select');
            
            formElements.forEach(element => {
                if (element.name && element.type !== 'file' && !element.hasAttribute('data-autosave-ignore')) {
                    if (element.name === '_token' || element.name === '_method') {
                        return;
                    }
                    if (element.type === 'checkbox' || element.type === 'radio') {
                        if (element.checked) {
                            formData[element.name] = element.value;
                        }
                    } else {
                        formData[element.name] = element.value;
                    }
                }
            });
            
            // Capturer les données du composant prediction-table-form
            this.capturePredictionTableData(formData);
            
            return formData;
        },
        
        capturePredictionTableData(formData) {
            // Émettre un événement global pour demander les données au composant prediction-table-form
            this.$root.$emit('request-prediction-data', (predictionData) => {
                if (predictionData) {
                    // Capturer les données reçues
                    if (predictionData.predictions && predictionData.predictions.length > 0) {
                        formData.predictions = JSON.stringify(predictionData.predictions);
                    }
                    
                    if (predictionData.lots && predictionData.lots.length > 0) {
                        formData.lots = JSON.stringify(predictionData.lots);
                    }
                    
                    // Capturer les autres propriétés
                    if (predictionData.quantity) formData.quantity = predictionData.quantity;
                    if (predictionData.unit) formData.unit = predictionData.unit;
                    if (predictionData.hours_per_day) formData.hours_per_day = predictionData.hours_per_day;
                    if (predictionData.margin_type) formData.margin_type = predictionData.margin_type;
                    if (predictionData.margin_workforce) formData.margin_workforce = predictionData.margin_workforce;
                    if (predictionData.margin_purchases) formData.margin_purchases = predictionData.margin_purchases;
                    if (predictionData.margin_stocks) formData.margin_stocks = predictionData.margin_stocks;
                    if (predictionData.margin_equipments) formData.margin_equipments = predictionData.margin_equipments;
                    if (predictionData.default_hourly_rate) formData.default_hourly_rate = predictionData.default_hourly_rate;
                }
            });
        },
        
        restoreFormData() {
            try {
                const savedData = localStorage.getItem(this.storageKey);
                if (savedData) {
                    const draftData = JSON.parse(savedData);
                    const formElements = this.form.querySelectorAll('input, textarea, select');
                    
                    formElements.forEach(element => {
                        if (element.name && !element.hasAttribute('data-autosave-ignore') && draftData.formData[element.name] !== undefined) {
                            if (element.name === '_token' || element.name === '_method') {
                                return;
                            }
                            if (element.type === 'checkbox' || element.type === 'radio') {
                                element.checked = (element.value === draftData.formData[element.name]);
                            } else {
                                element.value = draftData.formData[element.name];
                            }
                        }
                    });
                    
                    // Restaurer les données du composant prediction-table-form
                    this.restorePredictionTableData(draftData.formData);
                    
                    this.showToast('Données restaurées depuis le brouillon', 'info');
                    return true;
                }
            } catch (error) {
                console.error('Erreur lors de la restauration:', error);
            }
            return false;
        },
        
        restorePredictionTableData(formData) {
            // Émettre un événement global pour restaurer les données dans le composant prediction-table-form
            this.$root.$emit('restore-prediction-data', formData);
        },
        
        clearDraft() {
            localStorage.removeItem(this.storageKey);
            this.showToast('Brouillon supprimé', 'info');
        },
        
        hasDraft() {
            return localStorage.getItem(this.storageKey) !== null;
        },
        
        
        async showRestoreDialog() {
            const savedData = localStorage.getItem(this.storageKey);
            if (!savedData) return;
            
            this.draftDialogPending = true;

            const draftData = JSON.parse(savedData);
            const draftDate = new Date(draftData.timestamp).toLocaleString('fr-FR');
            
            const result = await Swal.fire({
                title: 'Brouillon trouvé',
                html: `Un brouillon a été trouvé datant du <strong>${draftDate}</strong>.<br>Voulez-vous le restaurer ?`,
                icon: 'question',
                showCancelButton: true,
                cancelButtonText: 'Ignorer',
                confirmButtonText: 'Restaurer',
                reverseButtons: true,
                heightAuto: false
            });
            
            if (result.isConfirmed) {
                this.restoreFormData();
            } else {
                // Proposer de supprimer le brouillon
                const deleteResult = await Swal.fire({
                    title: 'Supprimer le brouillon ?',
                    text: 'Voulez-vous supprimer ce brouillon ?',
                    icon: 'question',
                    showCancelButton: true,
                    cancelButtonText: 'Non',
                    confirmButtonText: 'Oui, supprimer',
                    reverseButtons: true,
                    heightAuto: false
                });
                
                if (deleteResult.isConfirmed) {
                    this.clearDraft();
                }
            }

            this.draftDialogPending = false;
            this.$root.$emit('draft-dialog-closed', result.isConfirmed);
        },
        async promptSave() {
           if (!this.isEnabled) return;
           if (!this.form) return;
           if (this.isPaused || this.draftDialogPending) return;

           // Sauvegarder automatiquement sans vérifier les champs obligatoires
           // car c'est un brouillon local
           this.saveFormData();
        }
    }
}
</script>